import {
  QConfigStructureCategory,
  QConfigStructureConditionType,
  QConfigStructureType,
} from '@exam-management/graphql';

import { type BaseQuestionConditionsContextType, type QConfigStructure } from '../../types';
import { getTopicAndSubtopicFormValues } from '../../utils';

import { type UpdateItemSetQuestionConditionsFormInput } from './schema';

function getContentTypeByChildrenStructures(childrenStructures: QConfigStructure[]) {
  const hasLOS = childrenStructures.every(
    (structure) =>
      structure.type === QConfigStructureType.Tag &&
      structure.category === QConfigStructureCategory.LearningObjectiveStatement,
  );
  if (hasLOS) return 'LOS';

  const hasTopic = childrenStructures.every(
    (structure) =>
      structure.type === QConfigStructureType.QBankStructureLevelItem &&
      structure.category === QConfigStructureCategory.Topic,
  );
  if (hasTopic) return 'TOPIC';

  throw new Error('Children structures must be either all LOS or all Topic');
}

// TODO: use tag id to check type instead of name
const DIFFICULTY_TAG_NAMES = ['ง่าย', 'กลาง', 'ยาก'];
const CALCULATED_TAG_NAMES = ['คำนวณ', 'ไม่คำนวณ'];

function formatConditions(conditions: QConfigStructure['conditions']) {
  const conditionsByCount = conditions.reduce(
    (acc, condition) => {
      const properties: NonNullable<
        UpdateItemSetQuestionConditionsFormInput['conditions'][number]
      >['properties'] = [];

      if (
        condition.type === QConfigStructureConditionType.MultiTagGroup &&
        'tagGroups' in condition
      ) {
        const { tagGroups } = condition;
        for (const tagGroup of tagGroups) {
          const { count, tags } = tagGroup;

          const difficultyTags = tags.find((tag) => DIFFICULTY_TAG_NAMES.includes(tag.name));
          const calculatedTags = tags.find((tag) => CALCULATED_TAG_NAMES.includes(tag.name));

          if (difficultyTags && calculatedTags) {
            for (let i = 0; i < count; i += 1) {
              properties.push({
                difficulty: { label: difficultyTags.name, value: difficultyTags.id },
                calculated: { label: calculatedTags.name, value: calculatedTags.id },
              });
            }
          }
        }

        acc.push({
          needTotal: condition.needTotal,
          properties,
        });
      }

      return acc;
    },
    [] as UpdateItemSetQuestionConditionsFormInput['conditions'],
  );

  return conditionsByCount;
}

export function getDefaultValuesForItemSetQuestionConditionsForm(
  input: Pick<
    BaseQuestionConditionsContextType,
    'structures' | 'levelItemsWithLevel' | 'checkExistOnStructures' | 'chapterData'
  > & {
    questionTypeStructureId: string;
  },
): UpdateItemSetQuestionConditionsFormInput {
  const { questionTypeStructureId, structures } = input;

  const questionTypeStructure = structures.find(
    (structure) => structure.id === questionTypeStructureId,
  );
  if (!questionTypeStructure) {
    return {
      location: {
        topics: {},
        subtopics: {},
      },
      scorePerQuestion: 0,
      itemSetType: 0,
      contentType: 'TOPIC',
      los: [],
      conditions: [],
    };
  }

  const questionTypeChildren = structures.filter(
    (structure) => structure.parentId === questionTypeStructureId,
  );
  const contentType = getContentTypeByChildrenStructures(questionTypeChildren);

  const topics: UpdateItemSetQuestionConditionsFormInput['location']['topics'] = {};
  const subtopics: UpdateItemSetQuestionConditionsFormInput['location']['subtopics'] = {};
  if (contentType === 'TOPIC') {
    const result = getTopicAndSubtopicFormValues(input);
    Object.assign(topics, result.topics);
    Object.assign(subtopics, result.subtopics);
  }

  let los: UpdateItemSetQuestionConditionsFormInput['los'] = [];
  if (contentType === 'LOS') {
    los = questionTypeChildren
      .filter(
        (structure) => structure.category === QConfigStructureCategory.LearningObjectiveStatement,
      )
      .map((structure) => ({
        label: structure.name,
        value: structure.refId,
      }))
      .filter((item): item is { label: string; value: string } =>
        Boolean(item.label && item.value),
      );
  }
  const scorePerQuestion = questionTypeStructure.score / questionTypeStructure.totalQuestions;

  return {
    itemSetType: questionTypeStructure.size ?? 0,
    contentType: getContentTypeByChildrenStructures(questionTypeChildren),
    location: {
      topics,
      subtopics,
    },
    los,
    conditions: formatConditions(questionTypeStructure.conditions),
    scorePerQuestion,
  };
}
